/**
 * Console Capture Utility
 *
 * Intercepts browser console output (log, warn, error, info, debug) and stores
 * the last MAX_ENTRIES entries in a ring buffer. Captured logs are attached to
 * support threads so the admin can see what was happening in the user's browser.
 *
 * Each entry is truncated to MAX_MESSAGE_LENGTH characters to keep the payload
 * reasonable when serialised.
 */

import type { ConsoleLogEntry } from "@a7y/types";
export type { ConsoleLogEntry };

export type ConsoleSubscriber = (entry: ConsoleLogEntry) => void;

const MAX_ENTRIES = 100;
const MAX_MESSAGE_LENGTH = 1000;
/**
 * Uncaught errors carry a stack, which the 1000-char console cap would cut
 * mid-frame. Matches `FEEDBACK_CAPTURE_LIMITS.maxConsoleMessageChars`, the
 * ceiling the API keeps (A7Y-5476).
 */
export const MAX_UNCAUGHT_MESSAGE_LENGTH = 4000;

const capturedLogs: ConsoleLogEntry[] = [];
const subscribers = new Set<ConsoleSubscriber>();

let initialised = false;

/**
 * Registers a subscriber that is called synchronously after every captured
 * console entry (regardless of level). Returns an unsubscribe function.
 *
 * Subscribers run inside a try/catch — a throwing subscriber will never break
 * normal console output or other subscribers.
 */
export function subscribeConsole(fn: ConsoleSubscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/**
 * Monkey-patches the five console methods so every call is captured in the ring
 * buffer while still forwarding to the original implementation.
 *
 * Safe to call multiple times — only the first invocation patches the console.
 */
export function initConsoleCapture(): void {
  if (initialised) return;
  initialised = true;

  const levels: ConsoleLogEntry["level"][] = ["log", "warn", "error", "info", "debug"];

  for (const level of levels) {
    const original = console[level];

    console[level] = (...args: unknown[]) => {
      try {
        const message = args
          .map((a) => {
            if (a instanceof Error) return `${a.name}: ${a.message}`;
            if (typeof a === "string") return a;
            try {
              return JSON.stringify(a);
            } catch {
              return String(a);
            }
          })
          .join(" ");

        const entry: ConsoleLogEntry = {
          level,
          message: message.length > MAX_MESSAGE_LENGTH
            ? message.slice(0, MAX_MESSAGE_LENGTH)
            : message,
          timestamp: Date.now(),
        };

        pushEntry(entry);

        // Notify subscribers. Each subscriber is isolated in its own
        // try/catch so a throwing subscriber NEVER breaks console output
        // or other subscribers.
        for (const sub of subscribers) {
          try {
            sub(entry);
          } catch {
            // Swallow — subscriber errors must not affect console behaviour.
          }
        }
      } catch {
        // Never let the capture logic interfere with normal console output
      }

      // Forward to the original console method
      original.apply(console, args);
    };
  }
}

/** Pushes onto the ring and evicts the oldest entries; does not notify subscribers. */
function pushEntry(entry: ConsoleLogEntry): void {
  capturedLogs.push(entry);
  if (capturedLogs.length > MAX_ENTRIES) {
    capturedLogs.splice(0, capturedLogs.length - MAX_ENTRIES);
  }
}

function describeThrown(value: unknown): { text: string; stack: string | null } {
  if (value instanceof Error) {
    return { text: `${value.name}: ${value.message}`, stack: value.stack ?? null };
  }
  if (typeof value === "string") return { text: value, stack: null };
  try {
    return { text: JSON.stringify(value) ?? String(value), stack: null };
  } catch {
    return { text: String(value), stack: null };
  }
}

function recordUncaught(prefix: string, value: unknown): void {
  const { text, stack } = describeThrown(value);
  // V8 stacks begin with "Name: message"; keep it once.
  const body = stack && stack.startsWith(text) ? stack : stack ? `${text}\n${stack}` : text;
  const message = `${prefix}${body}`;
  pushEntry({
    level: "error",
    message:
      message.length > MAX_UNCAUGHT_MESSAGE_LENGTH
        ? message.slice(0, MAX_UNCAUGHT_MESSAGE_LENGTH)
        : message,
    timestamp: Date.now(),
  });
}

let uncaughtInitialised = false;

/**
 * Records uncaught errors (`window` "error") and unhandled promise rejections,
 * with stack traces, in the same ring `initConsoleCapture` fills (A7Y-5476).
 * They reach `error-reporter.ts` through their own listeners; entries are
 * pushed WITHOUT notifying subscribers so the reporter, which subscribes to the
 * console stream, does not report the same failure twice.
 *
 * Safe to call multiple times.
 */
export function initUncaughtErrorCapture(): void {
  if (uncaughtInitialised || typeof window === "undefined") return;
  uncaughtInitialised = true;

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      try {
        recordUncaught("Uncaught ", event.error ?? event.message);
      } catch {
        // Never let capture interfere with the page.
      }
    },
    true
  );
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    try {
      recordUncaught("Unhandled rejection: ", event.reason);
    } catch {
      // Never let capture interfere with the page.
    }
  });
}

/**
 * Returns a shallow copy of the captured logs. The returned array is a snapshot
 * — it will not update as new logs are captured.
 */
export function getConsoleLogs(): ConsoleLogEntry[] {
  return [...capturedLogs];
}
